import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button, Input } from '@/components/common';
import {
  FiUser, FiDollarSign, FiFileText, FiCheckCircle, FiChevronRight,
  FiUsers, FiHome, FiBriefcase, FiTrendingUp, FiShield, FiClock,
  FiAlertCircle, FiMapPin, FiPhone, FiMail, FiCalendar, FiPlus, FiTrash2, FiX
} from 'react-icons/fi';
import { DisclaimerModal, SincerityDeclarationModal } from '@/components/modals';

// Types
interface ApplicationCategory {
  id: number;
  name: string;
  icon: React.ReactNode;
  completed: boolean;
  fields: string[];
}

// Styled Components
const ApplicationContainer = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: ${theme.spacing.xl};
  min-height: calc(100vh - 200px);

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  height: fit-content;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  box-shadow: ${theme.shadows.sm};
  position: sticky;
  top: ${theme.spacing.xl};

  /* Scrollbar personnalisée */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.colors.surface}50;
    border-radius: ${theme.borderRadius.full};
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary}40;
    border-radius: ${theme.borderRadius.full};

    &:hover {
      background: ${theme.colors.primary}60;
    }
  }
`;

const SidebarTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.surface};
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const CategoryItem = styled.button<{ $active: boolean; $completed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  background: ${props =>
    props.$active ? theme.colors.primary + '15' :
    props.$completed ? theme.colors.success + '10' :
    'transparent'};
  border: none;
  border-left: 3px solid ${props =>
    props.$active ? theme.colors.primary :
    props.$completed ? theme.colors.success :
    'transparent'};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  text-align: left;
  width: 100%;

  &:hover {
    background: ${props =>
      props.$active ? theme.colors.primary + '20' :
      theme.colors.surface};
  }

  svg {
    font-size: 18px;
    color: ${props =>
      props.$active ? theme.colors.primary :
      props.$completed ? theme.colors.success :
      theme.colors.text + '60'};
  }
`;

const CategoryNumber = styled.div<{ $active: boolean; $completed: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props =>
    props.$completed ? theme.colors.success :
    props.$active ? theme.colors.primary :
    theme.colors.surface};
  color: ${props =>
    props.$completed || props.$active ? 'white' :
    theme.colors.text + '60'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.bold};
  flex-shrink: 0;
`;

const CategoryName = styled.div<{ $active: boolean }>`
  flex: 1;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${props => props.$active ? theme.typography.weights.semibold : theme.typography.weights.normal};
  color: ${props => props.$active ? theme.colors.primary : theme.colors.text};
`;

const CompletionBadge = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.success};
`;

const FormContent = styled.div<{ $readonly?: boolean }>`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing['2xl']};
  box-shadow: ${theme.shadows.sm};

  /* Désactiver tous les inputs en mode readonly */
  ${props => props.$readonly && `
    input, select, textarea {
      background-color: ${theme.colors.surface}40;
      cursor: not-allowed;
      pointer-events: none;
    }
    button {
      pointer-events: none;
      opacity: 0.6;
    }
  `}
`;

const FormHeader = styled.div`
  margin-bottom: ${theme.spacing.xl};
  padding-bottom: ${theme.spacing.lg};
  border-bottom: 2px solid ${theme.colors.surface};
`;

const FormTitle = styled.h2`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  svg {
    font-size: 28px;
    color: ${theme.colors.primary};
  }
`;

const FormDescription = styled.p`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text}80;
  margin: 0;
`;

const FormSection = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const SectionTitle = styled.h4`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text};
  background: ${theme.colors.white};
  transition: border-color ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.base};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.text};
  background: ${theme.colors.white};
  resize: vertical;
  transition: border-color ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const FileUploadArea = styled.div`
  border: 2px dashed ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.xl};
  text-align: center;
  background: ${theme.colors.surface}30;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.primary}10;
  }
`;

const FileUploadText = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}70;
  margin: ${theme.spacing.sm} 0 0 0;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.xl};
  padding-top: ${theme.spacing.xl};
  border-top: 2px solid ${theme.colors.surface};
`;

const ProgressInfo = styled.div`
  margin-top: ${theme.spacing.lg};
  padding: ${theme.spacing.md};
  background: ${theme.colors.info}10;
  border-left: 3px solid ${theme.colors.info};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
`;

const DynamicEntryContainer = styled.div`
  border: 2px dashed ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.lg};
  position: relative;
  background: ${theme.colors.background};
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
    background: white;
  }
`;

const EntryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.surface};
`;

const EntryTitle = styled.h4`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const EntryNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: ${theme.colors.primary};
  color: white;
  border-radius: 50%;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.bold};
`;

const RemoveButton = styled.button`
  background: transparent;
  border: 1px solid ${theme.colors.error};
  color: ${theme.colors.error};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.error};
    color: white;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    font-size: 16px;
  }
`;

const AddButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.lg};
  background: ${theme.colors.accent}10;
  border: 2px dashed ${theme.colors.accent};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.accent};
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};
  margin-top: ${theme.spacing.lg};

  &:hover {
    background: ${theme.colors.accent};
    color: white;
    border-style: solid;
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }

  svg {
    font-size: 20px;
  }
`;

interface NewApplicationProps {
  readonly?: boolean;
  initialData?: any;
}

export const NewApplication: React.FC<NewApplicationProps> = ({
  readonly = false,
  initialData = null
}) => {
  const navigate = useNavigate();

  // États pour les modals de candidature (désactivés en mode readonly)
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(!readonly); // Affiché par défaut au chargement sauf en readonly
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(readonly); // Accepté automatiquement en readonly
  const [showSincerityModal, setShowSincerityModal] = useState(false); // Pour la soumission

  const [currentCategory, setCurrentCategory] = useState(1);

  const categories: ApplicationCategory[] = [
    { id: 1, name: 'Infos', icon: <FiUser />, completed: false, fields: [] },
    { id: 2, name: 'Animaux', icon: <FiHome />, completed: false, fields: [] },
    { id: 3, name: 'Emploi', icon: <FiBriefcase />, completed: false, fields: [] },
    { id: 4, name: 'Revenus', icon: <FiDollarSign />, completed: false, fields: [] },
    { id: 5, name: 'Épargne', icon: <FiTrendingUp />, completed: false, fields: [] },
    { id: 6, name: 'Garanties', icon: <FiShield />, completed: false, fields: [] },
    { id: 7, name: 'Garants', icon: <FiUsers />, completed: false, fields: [] },
    { id: 8, name: 'Loyer', icon: <FiClock />, completed: false, fields: [] },
    { id: 9, name: 'Assurance', icon: <FiShield />, completed: false, fields: [] },
    { id: 10, name: 'Autres', icon: <FiFileText />, completed: false, fields: [] },
    { id: 11, name: 'Dettes', icon: <FiAlertCircle />, completed: false, fields: [] },
    { id: 12, name: 'Résidence', icon: <FiMapPin />, completed: false, fields: [] },
    { id: 13, name: 'Paiements', icon: <FiDollarSign />, completed: false, fields: [] },
    { id: 14, name: 'Antécédents', icon: <FiAlertCircle />, completed: false, fields: [] },
    { id: 15, name: 'Références', icon: <FiPhone />, completed: false, fields: [] },
    { id: 16, name: 'Qualité', icon: <FiFileText />, completed: false, fields: [] },
    { id: 17, name: 'Résumé', icon: <FiCheckCircle />, completed: false, fields: [] },
  ];

  // États pour les listes dynamiques (catégories 3-15)
  const [professionalSituations, setProfessionalSituations] = useState([{
    id: 1,
    employer: '',
    current_professional_activity: '',
    prof_address: '',
    prof_phone: '',
    immediate_supervisor: '',
    position_held: '',
    date_debut: '',
    contract_type: '',
    net_salary: '',
    end_date_of_activity: '',
    reason_for_departure: '',
    prof_comments: '',
  }]);

  const [spouseProfessionalSituations, setSpouseProfessionalSituations] = useState([{
    id: 1,
    employer: '',
    current_professional_activity: '',
    position_held: '',
    contract_type: '',
    net_salary: '',
    date_debut: '',
  }]);

  const [incomes, setIncomes] = useState([{
    id: 1,
    nature: '',
    amount: '',
    frequency: '',
    comments: '',
  }]);

  const [spouseIncomes, setSpouseIncomes] = useState([{
    id: 1,
    nature: '',
    amount: '',
    frequency: '',
  }]);

  const [savings, setSavings] = useState([{
    id: 1,
    amount: '',
    institution: '',
    frequency: '',
    comments: '',
  }]);

  const [spouseSavings, setSpouseSavings] = useState([{
    id: 1,
    amount: '',
    institution: '',
  }]);

  const [financialGuarantees, setFinancialGuarantees] = useState([{
    id: 1,
    type: '',
    amount: '',
    payment_terms: '',
    specific_conditions: '',
    periodic_review: '',
  }]);

  const [spouseFinancialGuarantees, setSpouseFinancialGuarantees] = useState([{
    id: 1,
    type: '',
    amount: '',
  }]);

  const [personalGuarantors, setPersonalGuarantors] = useState([{
    id: 1,
    name: '',
    relationship: '',
    conditions: '',
  }]);

  const [spouseGuarantors, setSpouseGuarantors] = useState([{
    id: 1,
    name: '',
    relationship: '',
  }]);

  const [debts, setDebts] = useState([{
    id: 1,
    type: '',
    total_amount: '',
    due_date_amount: '',
    payment_frequency: '',
    start_date: '',
    end_date: '',
    comments: '',
  }]);

  const [residenceHistory, setResidenceHistory] = useState([{
    id: 1,
    address: '',
    city: '',
    monthly_rent: '',
    period: '',
    landlord_name: '',
    landlord_address: '',
    landlord_phone: '',
    landlord_email: '',
    reason_departure: '',
    comments: '',
  }]);

  const [references, setReferences] = useState([{
    id: 1,
    name: '',
    address: '',
    phone: '',
    email: '',
    relationship: '',
    comments: '',
  }]);

  const [formData, setFormData] = useState({
    // 1. Informations Personnelles
    last_name: '',
    first_name: '',
    birthday: '',
    nationality: '',
    phone: '',
    identification_document: null as File | null,
    address: '',
    city: '',
    email: '',
    postal_code: '',
    marital_status: '',
    number_of_dependents: '0',

    // Informations du conjoint (si marié)
    applying_with_spouse: false,
    spouse_last_name: '',
    spouse_first_name: '',
    spouse_birthday: '',
    spouse_nationality: '',
    spouse_phone: '',
    spouse_email: '',
    spouse_identification_document: null as File | null,

    // 2. Animaux de Compagnie
    pets_nature: '',
    pets_number: '0',

    // 4. Revenus Mensuels
    income_nature: '',
    income_amount: '',
    income_frequency: '',
    income_comments: '',

    // Revenus du conjoint
    spouse_income_nature: '',
    spouse_income_amount: '',
    spouse_income_frequency: '',

    // 5. Capacité d'Épargne
    savings_amount: '',
    financial_institution: '',
    savings_frequency: '',
    savings_comments: '',

    // Épargne du conjoint
    spouse_savings_amount: '',
    spouse_financial_institution: '',

    // 6. Garanties Financières
    financial_guarantee_type: '',
    financial_guarantee_amount: '',
    payment_terms: '',
    specific_conditions: '',
    periodic_review: '',

    // Garanties financières du conjoint
    spouse_financial_guarantee_type: '',
    spouse_financial_guarantee_amount: '',

    // 7. Garanties Personnelles
    guarantor_name: '',
    relationship_with_tenant: '',
    personal_guarantee_conditions: '',

    // Garant personnel du conjoint
    spouse_guarantor_name: '',
    spouse_relationship_with_guarantor: '',

    // 8. Loyer Anticipé
    advance_rent_months: '',
    advance_payment_terms: '',
    advance_specific_conditions: '',
    advance_periodic_review: '',

    // 9. Assurance Locative
    rental_insurance_details: '',
    coverage_amount: '',
    insurance_company: '',
    insurance_contacts: '',
    insurance_periodic_review: '',

    // 10. Autres Garanties
    other_guarantee_type: '',
    other_guarantee_amount: '',
    other_specific_conditions: '',

    // 11. Engagements Financiers/Dettes
    debt_type: '',
    total_amount: '',
    due_date_amount: '',
    payment_frequency: '',
    start_of_commitment: '',
    end_of_commitment: '',
    debt_comments: '',

    // 12. Historique de Résidence
    residence_address: '',
    residence_city: '',
    monthly_rent: '',
    period_of_residence: '',
    landlord_owner: '',
    landlord_address: '',
    landlord_phone: '',
    landlord_email: '',
    residence_reason_departure: '',
    residence_comments: '',

    // 13. Historique de Paiement de Loyer
    rent_payment_date: '',
    rent_payment_method: '',
    rent_debts: 'non',
    evicted: 'non',
    year_of_eviction: '',

    // 14. Antécédents Criminels
    nature_of_infraction: '',
    severity_of_infraction: '',
    date_of_infraction: '',
    type_of_conviction: '',
    rehabilitation_program: '',
    infraction_comments: '',

    // 15. Références Relationnelles
    reference_name: '',
    reference_address: '',
    reference_phone: '',
    reference_email: '',
    nature_of_relationship: '',
    reference_comments: '',
  });

  // Initialiser les données en mode readonly
  useEffect(() => {
    if (readonly && initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [readonly, initialData]);

  const handleNext = () => {
    if (currentCategory < categories.length) {
      setCurrentCategory(currentCategory + 1);
    }
  };

  const handlePrevious = () => {
    if (currentCategory > 1) {
      setCurrentCategory(currentCategory - 1);
    }
  };

  // Fonctions pour ajouter/supprimer des entrées dynamiques
  const addProfessionalSituation = () => {
    setProfessionalSituations([...professionalSituations, {
      id: professionalSituations.length + 1,
      employer: '', current_professional_activity: '', prof_address: '',
      prof_phone: '', immediate_supervisor: '', position_held: '',
      date_debut: '', contract_type: '', net_salary: '',
      end_date_of_activity: '', reason_for_departure: '', prof_comments: '',
    }]);
  };

  const removeProfessionalSituation = (id: number) => {
    if (professionalSituations.length > 1) {
      setProfessionalSituations(professionalSituations.filter(item => item.id !== id));
    }
  };

  const updateProfessionalSituation = (id: number, field: string, value: string) => {
    setProfessionalSituations(professionalSituations.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const addSpouseProfessionalSituation = () => {
    setSpouseProfessionalSituations([...spouseProfessionalSituations, {
      id: spouseProfessionalSituations.length + 1,
      employer: '', current_professional_activity: '',
      position_held: '',
      date_debut: '', contract_type: '', net_salary: ''
    } as any]);
  };

  const removeSpouseProfessionalSituation = (id: number) => {
    if (spouseProfessionalSituations.length > 1) {
      setSpouseProfessionalSituations(spouseProfessionalSituations.filter(item => item.id !== id));
    }
  };

  const updateSpouseProfessionalSituation = (id: number, field: string, value: string) => {
    setSpouseProfessionalSituations(spouseProfessionalSituations.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const addIncome = () => {
    setIncomes([...incomes, { id: incomes.length + 1, nature: '', amount: '', frequency: '' } as any]);
  };

  const removeIncome = (id: number) => {
    if (incomes.length > 1) setIncomes(incomes.filter(item => item.id !== id));
  };

  const updateIncome = (id: number, field: string, value: string) => {
    setIncomes(incomes.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addSpouseIncome = () => {
    setSpouseIncomes([...spouseIncomes, { id: spouseIncomes.length + 1, nature: '', amount: '', frequency: '' }]);
  };

  const removeSpouseIncome = (id: number) => {
    if (spouseIncomes.length > 1) setSpouseIncomes(spouseIncomes.filter(item => item.id !== id));
  };

  const updateSpouseIncome = (id: number, field: string, value: string) => {
    setSpouseIncomes(spouseIncomes.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addSaving = () => {
    setSavings([...savings, { id: savings.length + 1, amount: '', institution: '', frequency: '', comments: '' }]);
  };

  const removeSaving = (id: number) => {
    if (savings.length > 1) setSavings(savings.filter(item => item.id !== id));
  };

  const updateSaving = (id: number, field: string, value: string) => {
    setSavings(savings.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addSpouseSaving = () => {
    setSpouseSavings([...spouseSavings, { id: spouseSavings.length + 1, amount: '', institution: '' } as any]);
  };

  const removeSpouseSaving = (id: number) => {
    if (spouseSavings.length > 1) setSpouseSavings(spouseSavings.filter(item => item.id !== id));
  };

  const updateSpouseSaving = (id: number, field: string, value: string) => {
    setSpouseSavings(spouseSavings.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addFinancialGuarantee = () => {
    setFinancialGuarantees([...financialGuarantees, {
      id: financialGuarantees.length + 1, type: '', amount: '', payment_terms: '',
      specific_conditions: '', periodic_review: '',
    }]);
  };

  const removeFinancialGuarantee = (id: number) => {
    if (financialGuarantees.length > 1) setFinancialGuarantees(financialGuarantees.filter(item => item.id !== id));
  };

  const updateFinancialGuarantee = (id: number, field: string, value: string) => {
    setFinancialGuarantees(financialGuarantees.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addSpouseFinancialGuarantee = () => {
    setSpouseFinancialGuarantees([...spouseFinancialGuarantees, {
      id: spouseFinancialGuarantees.length + 1, type: '', amount: ''
    } as any]);
  };

  const removeSpouseFinancialGuarantee = (id: number) => {
    if (spouseFinancialGuarantees.length > 1) setSpouseFinancialGuarantees(spouseFinancialGuarantees.filter(item => item.id !== id));
  };

  const updateSpouseFinancialGuarantee = (id: number, field: string, value: string) => {
    setSpouseFinancialGuarantees(spouseFinancialGuarantees.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addPersonalGuarantor = () => {
    setPersonalGuarantors([...personalGuarantors, { id: personalGuarantors.length + 1, name: '', relationship: '', conditions: '' }]);
  };

  const removePersonalGuarantor = (id: number) => {
    if (personalGuarantors.length > 1) setPersonalGuarantors(personalGuarantors.filter(item => item.id !== id));
  };

  const updatePersonalGuarantor = (id: number, field: string, value: string) => {
    setPersonalGuarantors(personalGuarantors.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addSpousePersonalGuarantor = () => {
    setSpouseGuarantors([...spouseGuarantors, { id: spouseGuarantors.length + 1, name: '', relationship: '' }]);
  };

  const removeSpousePersonalGuarantor = (id: number) => {
    if (spouseGuarantors.length > 1) setSpouseGuarantors(spouseGuarantors.filter(item => item.id !== id));
  };

  const updateSpousePersonalGuarantor = (id: number, field: string, value: string) => {
    setSpouseGuarantors(spouseGuarantors.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addDebt = () => {
    setDebts([...debts, {
      id: debts.length + 1, type: '', total_amount: '', due_date_amount: '',
      payment_frequency: '', start_date: '', end_date: '', comments: '',
    }]);
  };

  const removeDebt = (id: number) => {
    if (debts.length > 1) setDebts(debts.filter(item => item.id !== id));
  };

  const updateDebt = (id: number, field: string, value: string) => {
    setDebts(debts.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addResidence = () => {
    setResidenceHistory([...residenceHistory, {
      id: residenceHistory.length + 1, address: '', city: '', monthly_rent: '', period: '',
      landlord_name: '', landlord_address: '', landlord_phone: '', landlord_email: '',
      reason_departure: '', comments: '',
    }]);
  };

  const removeResidence = (id: number) => {
    if (residenceHistory.length > 1) setResidenceHistory(residenceHistory.filter(item => item.id !== id));
  };

  const updateResidence = (id: number, field: string, value: string) => {
    setResidenceHistory(residenceHistory.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addReference = () => {
    setReferences([...references, {
      id: references.length + 1, name: '', address: '', phone: '', email: '', relationship: '', comments: '',
    }]);
  };

  const removeReference = (id: number) => {
    if (references.length > 1) setReferences(references.filter(item => item.id !== id));
  };

  const updateReference = (id: number, field: string, value: string) => {
    setReferences(references.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const handleSubmit = () => {
    // Ouvrir le modal de déclaration de sincérité au lieu de soumettre directement
    setShowSincerityModal(true);
  };

  // Fonction pour soumettre réellement après validation du modal de sincérité
  const handleFinalSubmit = () => {
    console.log('Submitting application:', formData);
    setShowSincerityModal(false);
    navigate('/candidate/applications');
  };

  // Handlers pour le modal de disclaimer
  const handleDisclaimerAccept = () => {
    setDisclaimerAccepted(true);
    setShowDisclaimerModal(false);
  };

  const handleDisclaimerDecline = () => {
    alert('Vous devez accepter les conditions pour continuer.');
    navigate('/candidate/dashboard');
  };

  // Handlers pour le modal de sincérité
  const handleSincerityValidate = () => {
    handleFinalSubmit();
  };

  const handleSincerityCancel = () => {
    setShowSincerityModal(false);
  };

  const renderCategoryForm = () => {
    console.log('🔍 DEBUG - currentCategory:', currentCategory, 'type:', typeof currentCategory);

    // Force conversion to number to ensure switch works correctly
    const categoryNum = Number(currentCategory);
    console.log('🔍 DEBUG - categoryNum:', categoryNum);

    switch (categoryNum) {
      case 1: // Informations Personnelles
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiUser />
                Informations Personnelles
              </FormTitle>
              <FormDescription>
                Veuillez renseigner vos informations personnelles complètes
              </FormDescription>
            </FormHeader>

            <FormGrid>
              <FormGroup>
                <Input
                  type="text"
                  label="Nom *"
                  value={formData.last_name}
                  onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  fullWidth
                />
              </FormGroup>

              <FormGroup>
                <Input
                  type="text"
                  label="Prénoms *"
                  value={formData.first_name}
                  onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  fullWidth
                />
              </FormGroup>

              <FormGroup>
                <Input
                  type="date"
                  label="Date de naissance *"
                  value={formData.birthday}
                  onChange={e => setFormData({ ...formData, birthday: e.target.value })}
                  required
                  fullWidth
                />
              </FormGroup>

              <FormGroup>
                <Input
                  type="text"
                  label="Nationalité *"
                  value={formData.nationality}
                  onChange={e => setFormData({ ...formData, nationality: e.target.value })}
                  required
                  fullWidth
                />
              </FormGroup>

              <FormGroup>
                <Input
                  type="tel"
                  label="Téléphone *"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  icon={<FiPhone />}
                  required
                  fullWidth
                />
              </FormGroup>

              <FormGroup>
                <Input
                  type="email"
                  label="Courriel *"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  icon={<FiMail />}
                  required
                  fullWidth
                />
              </FormGroup>
            </FormGrid>

            <FormGroup>
              <Input
                type="text"
                label="Adresse complète *"
                value={formData.address}
                onChange={e => setFormData({ ...formData, address: e.target.value })}
                icon={<FiMapPin />}
                required
                fullWidth
              />
            </FormGroup>

            <FormGrid>
              <FormGroup>
                <Input
                  type="text"
                  label="Ville *"
                  value={formData.city}
                  onChange={e => setFormData({ ...formData, city: e.target.value })}
                  required
                  fullWidth
                />
              </FormGroup>

              <FormGroup>
                <Input
                  type="text"
                  label="Code Postal *"
                  value={formData.postal_code}
                  onChange={e => setFormData({ ...formData, postal_code: e.target.value })}
                  required
                  fullWidth
                />
              </FormGroup>

              <FormGroup>
                <Label>Situation Familiale *</Label>
                <Select
                  value={formData.marital_status}
                  onChange={e => setFormData({ ...formData, marital_status: e.target.value })}
                  required
                >
                  <option value="">Sélectionner</option>
                  <option value="single">Célibataire</option>
                  <option value="married">Marié(e)</option>
                  <option value="divorced">Divorcé(e)</option>
                  <option value="widowed">Veuf/Veuve</option>
                  <option value="cohabiting">En concubinage</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Nombre de personnes à charge *</Label>
                <Select
                  value={formData.number_of_dependents}
                  onChange={e => setFormData({ ...formData, number_of_dependents: e.target.value })}
                  required
                >
                  <option value="0">0</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5+</option>
                </Select>
              </FormGroup>
            </FormGrid>

            <FormSection>
              <SectionTitle>Pièce d'identité</SectionTitle>
              <FileUploadArea>
                <FiFileText size={32} color={theme.colors.primary} />
                <FileUploadText>
                  Cliquez pour télécharger votre pièce d'identité (CNI, Passeport, etc.)
                </FileUploadText>
                <Input type="file" accept="image/*,application/pdf" fullWidth style={{ marginTop: theme.spacing.md }} />
              </FileUploadArea>
            </FormSection>

            {/* Section Conjoint si marié */}
            {formData.marital_status === 'married' && (
              <>
                <FormSection style={{ marginTop: theme.spacing.xl, paddingTop: theme.spacing.xl, borderTop: `2px solid ${theme.colors.surface}` }}>
                  <SectionTitle>
                    <FiUsers style={{ display: 'inline', marginRight: theme.spacing.sm }} />
                    Informations du Conjoint
                  </SectionTitle>

                  <FormGroup>
                    <Label style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                      <input
                        type="checkbox"
                        checked={formData.applying_with_spouse}
                        onChange={e => setFormData({ ...formData, applying_with_spouse: e.target.checked })}
                        style={{ width: '20px', height: '20px' }}
                      />
                      Je postule avec mon conjoint légalement marié
                    </Label>
                    <ProgressInfo style={{ marginTop: theme.spacing.sm }}>
                      ℹ️ Postuler avec votre conjoint permet de cumuler vos revenus pour améliorer votre score
                    </ProgressInfo>
                  </FormGroup>

                  {formData.applying_with_spouse && (
                    <>
                      <FormGrid style={{ marginTop: theme.spacing.lg }}>
                        <FormGroup>
                          <Input
                            type="text"
                            label="Nom du conjoint *"
                            value={formData.spouse_last_name}
                            onChange={e => setFormData({ ...formData, spouse_last_name: e.target.value })}
                            required
                            fullWidth
                          />
                        </FormGroup>

                        <FormGroup>
                          <Input
                            type="text"
                            label="Prénoms du conjoint *"
                            value={formData.spouse_first_name}
                            onChange={e => setFormData({ ...formData, spouse_first_name: e.target.value })}
                            required
                            fullWidth
                          />
                        </FormGroup>

                        <FormGroup>
                          <Input
                            type="date"
                            label="Date de naissance du conjoint *"
                            value={formData.spouse_birthday}
                            onChange={e => setFormData({ ...formData, spouse_birthday: e.target.value })}
                            icon={<FiCalendar />}
                            required
                            fullWidth
                          />
                        </FormGroup>

                        <FormGroup>
                          <Input
                            type="text"
                            label="Nationalité du conjoint *"
                            value={formData.spouse_nationality}
                            onChange={e => setFormData({ ...formData, spouse_nationality: e.target.value })}
                            required
                            fullWidth
                          />
                        </FormGroup>

                        <FormGroup>
                          <Input
                            type="tel"
                            label="Téléphone du conjoint *"
                            value={formData.spouse_phone}
                            onChange={e => setFormData({ ...formData, spouse_phone: e.target.value })}
                            icon={<FiPhone />}
                            required
                            fullWidth
                          />
                        </FormGroup>

                        <FormGroup>
                          <Input
                            type="email"
                            label="Email du conjoint *"
                            value={formData.spouse_email}
                            onChange={e => setFormData({ ...formData, spouse_email: e.target.value })}
                            icon={<FiMail />}
                            required
                            fullWidth
                          />
                        </FormGroup>
                      </FormGrid>

                      <FormGroup style={{ marginTop: theme.spacing.lg }}>
                        <Label>Pièce d'identité du conjoint *</Label>
                        <FileUploadArea>
                          <FiFileText size={24} color={theme.colors.primary} />
                          <FileUploadText>
                            Télécharger la pièce d'identité du conjoint (CNI, Passeport, etc.)
                          </FileUploadText>
                          <Input type="file" accept="image/*,application/pdf" fullWidth style={{ marginTop: theme.spacing.md }} />
                        </FileUploadArea>
                      </FormGroup>
                    </>
                  )}
                </FormSection>
              </>
            )}
          </>
        );

      case 2: // Animaux de Compagnie
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiHome />
                Animaux de Compagnie
              </FormTitle>
              <FormDescription>
                Indiquez si vous avez des animaux de compagnie
              </FormDescription>
            </FormHeader>

            <FormGrid>
              <FormGroup>
                <Label>Nature des animaux</Label>
                <Select
                  value={formData.pets_nature}
                  onChange={e => setFormData({ ...formData, pets_nature: e.target.value })}
                >
                  <option value="">Aucun animal</option>
                  <option value="dog">Chien</option>
                  <option value="cat">Chat</option>
                  <option value="bird">Oiseau</option>
                  <option value="fish">Poisson</option>
                  <option value="other">Autre</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Nombre d'animaux</Label>
                <Select
                  value={formData.pets_number}
                  onChange={e => setFormData({ ...formData, pets_number: e.target.value })}
                >
                  <option value="0">0</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3+</option>
                </Select>
              </FormGroup>
            </FormGrid>
          </>
        );

      case 3: // Situations Professionnelles
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiBriefcase />
                Situations Professionnelles
              </FormTitle>
              <FormDescription>
                Renseignez vos informations professionnelles (actuelles et passées)
              </FormDescription>
            </FormHeader>

            {/* Liste dynamique des situations professionnelles */}
            {professionalSituations.map((situation, index) => (
              <DynamicEntryContainer key={situation.id}>
                <EntryHeader>
                  <EntryTitle>
                    <FiBriefcase style={{ display: 'inline', marginRight: theme.spacing.xs }} />
                    Emploi <EntryNumber>#{index + 1}</EntryNumber>
                    {index === 0 && <span style={{ marginLeft: theme.spacing.sm, fontSize: theme.typography.sizes.xs, color: theme.colors.text + '60' }}>(Actuel)</span>}
                  </EntryTitle>
                  <RemoveButton
                    onClick={() => removeProfessionalSituation(situation.id)}
                    disabled={professionalSituations.length === 1}
                    title={professionalSituations.length === 1 ? "Vous devez avoir au moins une situation professionnelle" : "Supprimer cette situation"}
                  >
                    <FiTrash2 /> Retirer
                  </RemoveButton>
                </EntryHeader>

                <FormGrid>
                  <FormGroup>
                    <Input
                      type="text"
                      label="Employeur *"
                      value={situation.employer}
                      onChange={e => updateProfessionalSituation(situation.id, 'employer', e.target.value)}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="text"
                      label="Activité Professionnelle *"
                      value={situation.current_professional_activity}
                      onChange={e => updateProfessionalSituation(situation.id, 'current_professional_activity', e.target.value)}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="text"
                      label="Adresse de l'employeur"
                      value={situation.prof_address}
                      onChange={e => updateProfessionalSituation(situation.id, 'prof_address', e.target.value)}
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="tel"
                      label="Téléphone"
                      value={situation.prof_phone}
                      onChange={e => updateProfessionalSituation(situation.id, 'prof_phone', e.target.value)}
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="text"
                      label="Superviseur Immédiat"
                      value={situation.immediate_supervisor}
                      onChange={e => updateProfessionalSituation(situation.id, 'immediate_supervisor', e.target.value)}
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="text"
                      label="Position occupée *"
                      value={situation.position_held}
                      onChange={e => updateProfessionalSituation(situation.id, 'position_held', e.target.value)}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="date"
                      label="Date de début *"
                      value={situation.date_debut}
                      onChange={e => updateProfessionalSituation(situation.id, 'date_debut', e.target.value)}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Type de contrat *</Label>
                    <Select
                      value={situation.contract_type}
                      onChange={e => updateProfessionalSituation(situation.id, 'contract_type', e.target.value)}
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="cdi">CDI</option>
                      <option value="cdd">CDD</option>
                      <option value="interim">Intérim</option>
                      <option value="freelance">Freelance</option>
                      <option value="stage">Stage</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="number"
                      label="Salaire net mensuel ( FCFA) *"
                      value={situation.net_salary}
                      onChange={e => updateProfessionalSituation(situation.id, 'net_salary', e.target.value)}
                      icon={<FiDollarSign />}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="date"
                      label="Date de fin (si terminé)"
                      value={situation.end_date_of_activity}
                      onChange={e => updateProfessionalSituation(situation.id, 'end_date_of_activity', e.target.value)}
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="text"
                      label="Raison de départ"
                      value={situation.reason_for_departure}
                      onChange={e => updateProfessionalSituation(situation.id, 'reason_for_departure', e.target.value)}
                      fullWidth
                    />
                  </FormGroup>
                </FormGrid>

                <FormGroup>
                  <Label>Commentaires</Label>
                  <TextArea
                    value={situation.prof_comments}
                    onChange={e => updateProfessionalSituation(situation.id, 'prof_comments', e.target.value)}
                    placeholder="Informations complémentaires sur cet emploi..."
                  />
                </FormGroup>
              </DynamicEntryContainer>
            ))}

            <AddButton onClick={addProfessionalSituation}>
              <FiPlus /> Ajouter un autre emploi (historique)
            </AddButton>

            <FormSection style={{ marginTop: theme.spacing.xl }}>
              <SectionTitle>Documents professionnels</SectionTitle>
              <FileUploadArea>
                <FiFileText size={32} color={theme.colors.primary} />
                <FileUploadText>
                  Bulletins de salaire, contrats de travail, attestations d'emploi...
                </FileUploadText>
                <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
              </FileUploadArea>
            </FormSection>

            {/* Section Professionnelle du Conjoint */}
            {formData.applying_with_spouse && (
              <FormSection style={{ marginTop: theme.spacing.xl, paddingTop: theme.spacing.xl, borderTop: `2px solid ${theme.colors.surface}` }}>
                <SectionTitle>
                  <FiUsers style={{ display: 'inline', marginRight: theme.spacing.sm }} />
                  Situation Professionnelle du Conjoint
                </SectionTitle>

                {spouseProfessionalSituations.map((situation, index) => (
                  <DynamicEntryContainer key={situation.id} style={{ marginTop: index > 0 ? theme.spacing.lg : 0 }}>
                    <EntryHeader>
                      <EntryTitle>
                        <FiBriefcase style={{ display: 'inline', marginRight: theme.spacing.xs }} />
                        Emploi du conjoint <EntryNumber>#{index + 1}</EntryNumber>
                        {index === 0 && <span style={{ marginLeft: theme.spacing.sm, fontSize: theme.typography.sizes.xs, color: theme.colors.text + '60' }}>(Actuel)</span>}
                      </EntryTitle>
                      <RemoveButton
                        onClick={() => removeSpouseProfessionalSituation(situation.id)}
                        disabled={spouseProfessionalSituations.length === 1}
                        title={spouseProfessionalSituations.length === 1 ? "Le conjoint doit avoir au moins une situation professionnelle" : "Supprimer"}
                      >
                        <FiTrash2 /> Retirer
                      </RemoveButton>
                    </EntryHeader>

                    <FormGrid>
                      <FormGroup>
                        <Input
                          type="text"
                          label="Employeur *"
                          value={situation.employer}
                          onChange={e => updateSpouseProfessionalSituation(situation.id, 'employer', e.target.value)}
                          required
                          fullWidth
                        />
                      </FormGroup>

                      <FormGroup>
                        <Input
                          type="text"
                          label="Activité Professionnelle *"
                          value={situation.current_professional_activity}
                          onChange={e => updateSpouseProfessionalSituation(situation.id, 'current_professional_activity', e.target.value)}
                          required
                          fullWidth
                        />
                      </FormGroup>

                      <FormGroup>
                        <Input
                          type="text"
                          label="Position occupée *"
                          value={situation.position_held}
                          onChange={e => updateSpouseProfessionalSituation(situation.id, 'position_held', e.target.value)}
                          required
                          fullWidth
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>Type de contrat *</Label>
                        <Select
                          value={situation.contract_type}
                          onChange={e => updateSpouseProfessionalSituation(situation.id, 'contract_type', e.target.value)}
                          required
                        >
                          <option value="">Sélectionner</option>
                          <option value="cdi">CDI</option>
                          <option value="cdd">CDD</option>
                          <option value="interim">Intérim</option>
                          <option value="freelance">Freelance</option>
                          <option value="stage">Stage</option>
                        </Select>
                      </FormGroup>

                      <FormGroup>
                        <Input
                          type="number"
                          label="Salaire net mensuel ( FCFA) *"
                          value={situation.net_salary}
                          onChange={e => updateSpouseProfessionalSituation(situation.id, 'net_salary', e.target.value)}
                          icon={<FiDollarSign />}
                          required
                          fullWidth
                        />
                      </FormGroup>

                      <FormGroup>
                        <Input
                          type="date"
                          label="Date de début *"
                          value={situation.date_debut}
                          onChange={e => updateSpouseProfessionalSituation(situation.id, 'date_debut', e.target.value)}
                          required
                          fullWidth
                        />
                      </FormGroup>
                    </FormGrid>
                  </DynamicEntryContainer>
                ))}

                <AddButton onClick={addSpouseProfessionalSituation} style={{ marginTop: theme.spacing.lg }}>
                  <FiPlus /> Ajouter un autre emploi du conjoint
                </AddButton>

                <FormGroup style={{ marginTop: theme.spacing.lg }}>
                  <Label>Documents professionnels du conjoint</Label>
                  <FileUploadArea>
                    <FiFileText size={24} color={theme.colors.primary} />
                    <FileUploadText>
                      Bulletins de salaire du conjoint, contrat de travail...
                    </FileUploadText>
                    <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
                  </FileUploadArea>
                </FormGroup>

                <ProgressInfo style={{ marginTop: theme.spacing.md }}>
                  💰 Revenu total du foyer: {(
                    professionalSituations.reduce((sum, s) => sum + parseFloat(s.net_salary || '0'), 0) +
                    spouseProfessionalSituations.reduce((sum, s) => sum + parseFloat(s.net_salary || '0'), 0)
                  ).toLocaleString()}  FCFA/mois
                </ProgressInfo>
              </FormSection>
            )}
          </>
        );

      case 4: // Revenus Mensuels
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiDollarSign />
                Revenus Mensuels
              </FormTitle>
              <FormDescription>
                Détaillez toutes vos sources de revenus mensuels
              </FormDescription>
            </FormHeader>

            {/* Liste dynamique des revenus */}
            {incomes.map((income, index) => (
              <DynamicEntryContainer key={income.id}>
                <EntryHeader>
                  <EntryTitle>
                    <FiDollarSign style={{ display: 'inline', marginRight: theme.spacing.xs }} />
                    Source de revenu <EntryNumber>#{index + 1}</EntryNumber>
                  </EntryTitle>
                  <RemoveButton
                    onClick={() => removeIncome(income.id)}
                    disabled={incomes.length === 1}
                    title={incomes.length === 1 ? "Vous devez avoir au moins une source de revenu" : "Supprimer cette source"}
                  >
                    <FiTrash2 /> Retirer
                  </RemoveButton>
                </EntryHeader>

                <FormGrid>
                  <FormGroup>
                    <Label>Nature des revenus *</Label>
                    <Select
                      value={income.nature}
                      onChange={e => updateIncome(income.id, 'nature', e.target.value)}
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="salary">Salaire</option>
                      <option value="pension">Pension de retraite</option>
                      <option value="disability">Pension d'invalidité</option>
                      <option value="unemployment">Allocations chômage</option>
                      <option value="rental">Revenus locatifs</option>
                      <option value="investment">Revenus d'investissement</option>
                      <option value="business">Revenus d'entreprise</option>
                      <option value="other">Autre</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="number"
                      label="Montant ( FCFA) *"
                      value={income.amount}
                      onChange={e => updateIncome(income.id, 'amount', e.target.value)}
                      icon={<FiDollarSign />}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Fréquence *</Label>
                    <Select
                      value={income.frequency}
                      onChange={e => updateIncome(income.id, 'frequency', e.target.value)}
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="monthly">Mensuel</option>
                      <option value="quarterly">Trimestriel</option>
                      <option value="annual">Annuel</option>
                      <option value="irregular">Irrégulier</option>
                    </Select>
                  </FormGroup>
                </FormGrid>

                <FormGroup>
                  <Label>Commentaires</Label>
                  <TextArea
                    value={income.comments}
                    onChange={e => updateIncome(income.id, 'comments', e.target.value)}
                    placeholder="Précisions sur cette source de revenu..."
                  />
                </FormGroup>
              </DynamicEntryContainer>
            ))}

            <AddButton onClick={addIncome}>
              <FiPlus /> Ajouter une autre source de revenu
            </AddButton>

            <FormSection style={{ marginTop: theme.spacing.xl }}>
              <SectionTitle>Justificatifs de revenus</SectionTitle>
              <FileUploadArea>
                <FiFileText size={32} color={theme.colors.primary} />
                <FileUploadText>
                  Bulletins de salaire, avis d'imposition, relevés bancaires...
                </FileUploadText>
                <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
              </FileUploadArea>
            </FormSection>

            {/* Section Revenus du Conjoint */}
            {formData.applying_with_spouse && (
              <FormSection style={{ marginTop: theme.spacing.xl, paddingTop: theme.spacing.xl, borderTop: `2px solid ${theme.colors.surface}` }}>
                <SectionTitle>
                  <FiUsers style={{ display: 'inline', marginRight: theme.spacing.sm }} />
                  Revenus Mensuels du Conjoint
                </SectionTitle>

                {spouseIncomes.map((income, index) => (
                  <DynamicEntryContainer key={income.id} style={{ marginTop: index > 0 ? theme.spacing.lg : 0 }}>
                    <EntryHeader>
                      <EntryTitle>
                        <FiDollarSign style={{ display: 'inline', marginRight: theme.spacing.xs }} />
                        Source de revenu du conjoint <EntryNumber>#{index + 1}</EntryNumber>
                      </EntryTitle>
                      <RemoveButton
                        onClick={() => removeSpouseIncome(income.id)}
                        disabled={spouseIncomes.length === 1}
                        title={spouseIncomes.length === 1 ? "Le conjoint doit avoir au moins une source de revenu" : "Supprimer"}
                      >
                        <FiTrash2 /> Retirer
                      </RemoveButton>
                    </EntryHeader>

                    <FormGrid>
                      <FormGroup>
                        <Label>Nature des revenus *</Label>
                        <Select
                          value={income.nature}
                          onChange={e => updateSpouseIncome(income.id, 'nature', e.target.value)}
                          required
                        >
                          <option value="">Sélectionner</option>
                          <option value="salary">Salaire</option>
                          <option value="pension">Pension de retraite</option>
                          <option value="disability">Pension d'invalidité</option>
                          <option value="unemployment">Allocations chômage</option>
                          <option value="rental">Revenus locatifs</option>
                          <option value="investment">Revenus d'investissement</option>
                          <option value="business">Revenus d'entreprise</option>
                          <option value="other">Autre</option>
                        </Select>
                      </FormGroup>

                      <FormGroup>
                        <Input
                          type="number"
                          label="Montant ( FCFA) *"
                          value={income.amount}
                          onChange={e => updateSpouseIncome(income.id, 'amount', e.target.value)}
                          icon={<FiDollarSign />}
                          required
                          fullWidth
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>Fréquence *</Label>
                        <Select
                          value={income.frequency}
                          onChange={e => updateSpouseIncome(income.id, 'frequency', e.target.value)}
                          required
                        >
                          <option value="">Sélectionner</option>
                          <option value="monthly">Mensuel</option>
                          <option value="quarterly">Trimestriel</option>
                          <option value="annual">Annuel</option>
                          <option value="irregular">Irrégulier</option>
                        </Select>
                      </FormGroup>
                    </FormGrid>

                    <FormGroup>
                      <Label>Commentaires</Label>
                      <TextArea
                        value={(income as any).comments}
                        onChange={e => updateSpouseIncome(income.id, 'comments', e.target.value)}
                        placeholder="Précisions sur cette source de revenu du conjoint..."
                      />
                    </FormGroup>
                  </DynamicEntryContainer>
                ))}

                <AddButton onClick={addSpouseIncome} style={{ marginTop: theme.spacing.lg }}>
                  <FiPlus /> Ajouter une autre source de revenu du conjoint
                </AddButton>

                <FormGroup style={{ marginTop: theme.spacing.lg }}>
                  <Label>Justificatifs de revenus du conjoint</Label>
                  <FileUploadArea>
                    <FiFileText size={24} color={theme.colors.primary} />
                    <FileUploadText>
                      Bulletins de salaire, avis d'imposition du conjoint...
                    </FileUploadText>
                    <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
                  </FileUploadArea>
                </FormGroup>

                <ProgressInfo style={{ marginTop: theme.spacing.md }}>
                  💰 Revenus totaux du foyer: {(
                    incomes.reduce((sum, i) => sum + parseFloat(i.amount || '0'), 0) +
                    spouseIncomes.reduce((sum, i) => sum + parseFloat(i.amount || '0'), 0)
                  ).toLocaleString()}  FCFA/mois
                </ProgressInfo>
              </FormSection>
            )}
          </>
        );

      case 5: // Capacité d'Épargne
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiTrendingUp />
                Capacité d'Épargne
              </FormTitle>
              <FormDescription>
                Indiquez toutes vos capacités d'épargne et réserves financières
              </FormDescription>
            </FormHeader>

            {/* Liste dynamique des épargnes */}
            {savings.map((saving, index) => (
              <DynamicEntryContainer key={saving.id}>
                <EntryHeader>
                  <EntryTitle>
                    <FiTrendingUp style={{ display: 'inline', marginRight: theme.spacing.xs }} />
                    Compte d'épargne <EntryNumber>#{index + 1}</EntryNumber>
                  </EntryTitle>
                  <RemoveButton
                    onClick={() => removeSaving(saving.id)}
                    disabled={savings.length === 1}
                    title={savings.length === 1 ? "Vous devez avoir au moins un compte d'épargne" : "Supprimer ce compte"}
                  >
                    <FiTrash2 /> Retirer
                  </RemoveButton>
                </EntryHeader>

                <FormGrid>
                  <FormGroup>
                    <Input
                      type="number"
                      label="Montant de l'épargne ( FCFA) *"
                      value={saving.amount}
                      onChange={e => updateSaving(saving.id, 'amount', e.target.value)}
                      icon={<FiDollarSign />}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="text"
                      label="Institution financière *"
                      value={saving.institution}
                      onChange={e => updateSaving(saving.id, 'institution', e.target.value)}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Fréquence d'épargne</Label>
                    <Select
                      value={saving.frequency}
                      onChange={e => updateSaving(saving.id, 'frequency', e.target.value)}
                    >
                      <option value="">Sélectionner</option>
                      <option value="monthly">Mensuel</option>
                      <option value="quarterly">Trimestriel</option>
                      <option value="annual">Annuel</option>
                      <option value="irregular">Irrégulier</option>
                      <option value="none">Aucune épargne régulière</option>
                    </Select>
                  </FormGroup>
                </FormGrid>

                <FormGroup>
                  <Label>Commentaires</Label>
                  <TextArea
                    value={saving.comments}
                    onChange={e => updateSaving(saving.id, 'comments', e.target.value)}
                    placeholder="Informations complémentaires sur cette épargne..."
                  />
                </FormGroup>
              </DynamicEntryContainer>
            ))}

            <AddButton onClick={addSaving}>
              <FiPlus /> Ajouter un autre compte d'épargne
            </AddButton>

            <FormSection style={{ marginTop: theme.spacing.xl }}>
              <SectionTitle>Justificatifs bancaires</SectionTitle>
              <FileUploadArea>
                <FiFileText size={32} color={theme.colors.primary} />
                <FileUploadText>
                  Relevés bancaires, attestations d'épargne...
                </FileUploadText>
                <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
              </FileUploadArea>
            </FormSection>

            {/* Section Épargne du Conjoint */}
            {formData.applying_with_spouse && (
              <FormSection style={{ marginTop: theme.spacing.xl, paddingTop: theme.spacing.xl, borderTop: `2px solid ${theme.colors.surface}` }}>
                <SectionTitle>
                  <FiUsers style={{ display: 'inline', marginRight: theme.spacing.sm }} />
                  Capacité d'Épargne du Conjoint
                </SectionTitle>

                {spouseSavings.map((saving, index) => (
                  <DynamicEntryContainer key={saving.id} style={{ marginTop: index > 0 ? theme.spacing.lg : 0 }}>
                    <EntryHeader>
                      <EntryTitle>
                        <FiTrendingUp style={{ display: 'inline', marginRight: theme.spacing.xs }} />
                        Compte d'épargne du conjoint <EntryNumber>#{index + 1}</EntryNumber>
                      </EntryTitle>
                      <RemoveButton
                        onClick={() => removeSpouseSaving(saving.id)}
                        disabled={spouseSavings.length === 1}
                        title={spouseSavings.length === 1 ? "Le conjoint doit avoir au moins un compte d'épargne" : "Supprimer"}
                      >
                        <FiTrash2 /> Retirer
                      </RemoveButton>
                    </EntryHeader>

                    <FormGrid>
                      <FormGroup>
                        <Input
                          type="number"
                          label="Montant ( FCFA) *"
                          value={saving.amount}
                          onChange={e => updateSpouseSaving(saving.id, 'amount', e.target.value)}
                          icon={<FiDollarSign />}
                          required
                          fullWidth
                        />
                      </FormGroup>

                      <FormGroup>
                        <Input
                          type="text"
                          label="Institution financière *"
                          value={saving.institution}
                          onChange={e => updateSpouseSaving(saving.id, 'institution', e.target.value)}
                          required
                          fullWidth
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>Fréquence d'épargne</Label>
                        <Select
                          value={(saving as any).frequency}
                          onChange={e => updateSpouseSaving(saving.id, 'frequency', e.target.value)}
                        >
                          <option value="">Sélectionner</option>
                          <option value="monthly">Mensuel</option>
                          <option value="quarterly">Trimestriel</option>
                          <option value="annual">Annuel</option>
                          <option value="irregular">Irrégulier</option>
                          <option value="none">Aucune épargne régulière</option>
                        </Select>
                      </FormGroup>
                    </FormGrid>

                    <FormGroup>
                      <Label>Commentaires</Label>
                      <TextArea
                        value={(saving as any).comments}
                        onChange={e => updateSpouseSaving(saving.id, 'comments', e.target.value)}
                        placeholder="Informations sur cette épargne du conjoint..."
                      />
                    </FormGroup>
                  </DynamicEntryContainer>
                ))}

                <AddButton onClick={addSpouseSaving} style={{ marginTop: theme.spacing.lg }}>
                  <FiPlus /> Ajouter un autre compte du conjoint
                </AddButton>

                <FormGroup style={{ marginTop: theme.spacing.lg }}>
                  <Label>Justificatifs bancaires du conjoint</Label>
                  <FileUploadArea>
                    <FiFileText size={24} color={theme.colors.primary} />
                    <FileUploadText>
                      Relevés bancaires, attestations d'épargne du conjoint...
                    </FileUploadText>
                    <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
                  </FileUploadArea>
                </FormGroup>

                <ProgressInfo style={{ marginTop: theme.spacing.md }}>
                  💰 Épargne totale du foyer: {(
                    (savings || []).reduce((sum, s) => sum + parseFloat(s.amount || '0'), 0) +
                    (spouseSavings || []).reduce((sum, s) => sum + parseFloat(s.amount || '0'), 0)
                  ).toLocaleString()}  FCFA
                </ProgressInfo>
              </FormSection>
            )}
          </>
        );

      case 6: // Garanties Financières
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiShield />
                Garanties Financières
              </FormTitle>
              <FormDescription>
                Renseignez toutes vos garanties financières pour la location
              </FormDescription>
            </FormHeader>

            {/* Liste dynamique des garanties financières */}
            {(financialGuarantees || []).map((guarantee, index) => (
              <DynamicEntryContainer key={guarantee.id}>
                <EntryHeader>
                  <EntryTitle>
                    <FiShield style={{ display: 'inline', marginRight: theme.spacing.xs }} />
                    Garantie financière <EntryNumber>#{index + 1}</EntryNumber>
                  </EntryTitle>
                  <RemoveButton
                    onClick={() => removeFinancialGuarantee(guarantee.id)}
                    disabled={financialGuarantees.length === 1}
                    title={financialGuarantees.length === 1 ? "Vous devez avoir au moins une garantie" : "Supprimer cette garantie"}
                  >
                    <FiTrash2 /> Retirer
                  </RemoveButton>
                </EntryHeader>

                <FormGrid>
                  <FormGroup>
                    <Label>Type de garantie *</Label>
                    <Select
                      value={guarantee.type}
                      onChange={e => updateFinancialGuarantee(guarantee.id, 'type', e.target.value)}
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="deposit">Dépôt de garantie</option>
                      <option value="bank_guarantee">Garantie bancaire</option>
                      <option value="insurance">Assurance loyers impayés</option>
                      <option value="solidarity_fund">Fonds de solidarité</option>
                      <option value="visale">Garantie Visale</option>
                      <option value="other">Autre</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="number"
                      label="Montant ( FCFA) *"
                      value={guarantee.amount}
                      onChange={e => updateFinancialGuarantee(guarantee.id, 'amount', e.target.value)}
                      icon={<FiDollarSign />}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Modalités de paiement</Label>
                    <Select
                      value={guarantee.payment_terms}
                      onChange={e => updateFinancialGuarantee(guarantee.id, 'payment_terms', e.target.value)}
                    >
                      <option value="">Sélectionner</option>
                      <option value="lump_sum">Paiement unique</option>
                      <option value="installments">Paiements échelonnés</option>
                      <option value="monthly">Mensuel</option>
                      <option value="quarterly">Trimestriel</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Révision périodique</Label>
                    <Select
                      value={guarantee.periodic_review}
                      onChange={e => updateFinancialGuarantee(guarantee.id, 'periodic_review', e.target.value)}
                    >
                      <option value="">Sélectionner</option>
                      <option value="monthly">Mensuelle</option>
                      <option value="quarterly">Trimestrielle</option>
                      <option value="semi_annual">Semestrielle</option>
                      <option value="annual">Annuelle</option>
                      <option value="none">Aucune</option>
                    </Select>
                  </FormGroup>
                </FormGrid>

                <FormGroup>
                  <Label>Conditions spécifiques</Label>
                  <TextArea
                    value={guarantee.specific_conditions}
                    onChange={e => updateFinancialGuarantee(guarantee.id, 'specific_conditions', e.target.value)}
                    placeholder="Conditions particulières de cette garantie..."
                  />
                </FormGroup>
              </DynamicEntryContainer>
            ))}

            <AddButton onClick={addFinancialGuarantee}>
              <FiPlus /> Ajouter une autre garantie financière
            </AddButton>

            <FormSection style={{ marginTop: theme.spacing.xl }}>
              <SectionTitle>Documents de garantie</SectionTitle>
              <FileUploadArea>
                <FiFileText size={32} color={theme.colors.primary} />
                <FileUploadText>
                  Attestations de garantie, contrats, justificatifs...
                </FileUploadText>
                <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
              </FileUploadArea>
            </FormSection>

            {/* Section Garanties Financières du Conjoint */}
            {formData.applying_with_spouse && (
              <FormSection style={{ marginTop: theme.spacing.xl, paddingTop: theme.spacing.xl, borderTop: `2px solid ${theme.colors.surface}` }}>
                <SectionTitle>
                  <FiUsers style={{ display: 'inline', marginRight: theme.spacing.sm }} />
                  Garanties Financières du Conjoint
                </SectionTitle>

                {spouseFinancialGuarantees.map((guarantee, index) => (
                  <DynamicEntryContainer key={guarantee.id} style={{ marginTop: index > 0 ? theme.spacing.lg : 0 }}>
                    <EntryHeader>
                      <EntryTitle>
                        <FiShield style={{ display: 'inline', marginRight: theme.spacing.xs }} />
                        Garantie du conjoint <EntryNumber>#{index + 1}</EntryNumber>
                      </EntryTitle>
                      <RemoveButton
                        onClick={() => removeSpouseFinancialGuarantee(guarantee.id)}
                        disabled={spouseFinancialGuarantees.length === 1}
                        title={spouseFinancialGuarantees.length === 1 ? "Le conjoint doit avoir au moins une garantie" : "Supprimer"}
                      >
                        <FiTrash2 /> Retirer
                      </RemoveButton>
                    </EntryHeader>

                    <FormGrid>
                      <FormGroup>
                        <Label>Type de garantie *</Label>
                        <Select
                          value={guarantee.type}
                          onChange={e => updateSpouseFinancialGuarantee(guarantee.id, 'type', e.target.value)}
                          required
                        >
                          <option value="">Sélectionner</option>
                          <option value="deposit">Dépôt de garantie</option>
                          <option value="bank_guarantee">Garantie bancaire</option>
                          <option value="insurance">Assurance loyers impayés</option>
                          <option value="solidarity_fund">Fonds de solidarité</option>
                          <option value="visale">Garantie Visale</option>
                          <option value="other">Autre</option>
                        </Select>
                      </FormGroup>

                      <FormGroup>
                        <Input
                          type="number"
                          label="Montant ( FCFA) *"
                          value={guarantee.amount}
                          onChange={e => updateSpouseFinancialGuarantee(guarantee.id, 'amount', e.target.value)}
                          icon={<FiDollarSign />}
                          required
                          fullWidth
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>Modalités de paiement</Label>
                        <Select
                          value={(guarantee as any).payment_terms}
                          onChange={e => updateSpouseFinancialGuarantee(guarantee.id, 'payment_terms', e.target.value)}
                        >
                          <option value="">Sélectionner</option>
                          <option value="lump_sum">Paiement unique</option>
                          <option value="installments">Paiements échelonnés</option>
                          <option value="monthly">Mensuel</option>
                          <option value="quarterly">Trimestriel</option>
                        </Select>
                      </FormGroup>
                    </FormGrid>

                    <FormGroup>
                      <Label>Conditions spécifiques</Label>
                      <TextArea
                        value={(guarantee as any).specific_conditions}
                        onChange={e => updateSpouseFinancialGuarantee(guarantee.id, 'specific_conditions', e.target.value)}
                        placeholder="Conditions particulières..."
                      />
                    </FormGroup>
                  </DynamicEntryContainer>
                ))}

                <AddButton onClick={addSpouseFinancialGuarantee} style={{ marginTop: theme.spacing.lg }}>
                  <FiPlus /> Ajouter une autre garantie du conjoint
                </AddButton>

                <FormGroup style={{ marginTop: theme.spacing.lg }}>
                  <Label>Documents de garantie du conjoint</Label>
                  <FileUploadArea>
                    <FiFileText size={24} color={theme.colors.primary} />
                    <FileUploadText>
                      Attestations de garantie du conjoint...
                    </FileUploadText>
                    <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
                  </FileUploadArea>
                </FormGroup>
              </FormSection>
            )}
          </>
        );

      case 7: // Garanties Personnelles
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiUsers />
                Garanties Personnelles
              </FormTitle>
              <FormDescription>
                Informations sur tous vos garants personnels
              </FormDescription>
            </FormHeader>

            {/* Liste dynamique des garants personnels */}
            {personalGuarantors.map((guarantor, index) => (
              <DynamicEntryContainer key={guarantor.id}>
                <EntryHeader>
                  <EntryTitle>
                    <FiUsers style={{ display: 'inline', marginRight: theme.spacing.xs }} />
                    Garant personnel <EntryNumber>#{index + 1}</EntryNumber>
                  </EntryTitle>
                  <RemoveButton
                    onClick={() => removePersonalGuarantor(guarantor.id)}
                    disabled={personalGuarantors.length === 1}
                    title={personalGuarantors.length === 1 ? "Vous devez avoir au moins un garant" : "Supprimer ce garant"}
                  >
                    <FiTrash2 /> Retirer
                  </RemoveButton>
                </EntryHeader>

                <FormGrid>
                  <FormGroup>
                    <Input
                      type="text"
                      label="Nom du garant *"
                      value={guarantor.name}
                      onChange={e => updatePersonalGuarantor(guarantor.id, 'name', e.target.value)}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Relation avec le locataire *</Label>
                    <Select
                      value={guarantor.relationship}
                      onChange={e => updatePersonalGuarantor(guarantor.id, 'relationship', e.target.value)}
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="parent">Parent</option>
                      <option value="sibling">Frère/Sœur</option>
                      <option value="spouse">Conjoint(e)</option>
                      <option value="friend">Ami(e)</option>
                      <option value="employer">Employeur</option>
                      <option value="other_family">Autre membre de la famille</option>
                      <option value="other">Autre</option>
                    </Select>
                  </FormGroup>
                </FormGrid>

                <FormGroup>
                  <Label>Conditions de la garantie personnelle</Label>
                  <TextArea
                    value={guarantor.conditions}
                    onChange={e => updatePersonalGuarantor(guarantor.id, 'conditions', e.target.value)}
                    placeholder="Détails sur l'engagement de ce garant..."
                  />
                </FormGroup>
              </DynamicEntryContainer>
            ))}

            <AddButton onClick={addPersonalGuarantor}>
              <FiPlus /> Ajouter un autre garant personnel
            </AddButton>

            <FormSection style={{ marginTop: theme.spacing.xl }}>
              <SectionTitle>Documents des garants</SectionTitle>
              <FileUploadArea>
                <FiFileText size={32} color={theme.colors.primary} />
                <FileUploadText>
                  Pièces d'identité, justificatifs de revenus, avis d'imposition des garants...
                </FileUploadText>
                <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
              </FileUploadArea>
            </FormSection>

            {/* Section Garant Personnel du Conjoint */}
            {formData.applying_with_spouse && (
              <FormSection style={{ marginTop: theme.spacing.xl, paddingTop: theme.spacing.xl, borderTop: `2px solid ${theme.colors.surface}` }}>
                <SectionTitle>
                  <FiUsers style={{ display: 'inline', marginRight: theme.spacing.sm }} />
                  Garants Personnels du Conjoint
                </SectionTitle>

                {spouseGuarantors.map((guarantor, index) => (
                  <DynamicEntryContainer key={guarantor.id} style={{ marginTop: index > 0 ? theme.spacing.lg : 0 }}>
                    <EntryHeader>
                      <EntryTitle>
                        <FiUsers style={{ display: 'inline', marginRight: theme.spacing.xs }} />
                        Garant du conjoint <EntryNumber>#{index + 1}</EntryNumber>
                      </EntryTitle>
                      <RemoveButton
                        onClick={() => removeSpousePersonalGuarantor(guarantor.id)}
                        disabled={spouseGuarantors.length === 1}
                        title={spouseGuarantors.length === 1 ? "Le conjoint doit avoir au moins un garant" : "Supprimer"}
                      >
                        <FiTrash2 /> Retirer
                      </RemoveButton>
                    </EntryHeader>

                    <FormGrid>
                      <FormGroup>
                        <Input
                          type="text"
                          label="Nom du garant *"
                          value={guarantor.name}
                          onChange={e => updateSpousePersonalGuarantor(guarantor.id, 'name', e.target.value)}
                          required
                          fullWidth
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>Relation avec le conjoint *</Label>
                        <Select
                          value={guarantor.relationship}
                          onChange={e => updateSpousePersonalGuarantor(guarantor.id, 'relationship', e.target.value)}
                          required
                        >
                          <option value="">Sélectionner</option>
                          <option value="parent">Parent</option>
                          <option value="sibling">Frère/Sœur</option>
                          <option value="friend">Ami(e)</option>
                          <option value="employer">Employeur</option>
                          <option value="other_family">Autre membre de la famille</option>
                          <option value="other">Autre</option>
                        </Select>
                      </FormGroup>
                    </FormGrid>

                    <FormGroup>
                      <Label>Conditions</Label>
                      <TextArea
                        value={(guarantor as any).conditions}
                        onChange={e => updateSpousePersonalGuarantor(guarantor.id, 'conditions', e.target.value)}
                        placeholder="Détails sur l'engagement..."
                      />
                    </FormGroup>
                  </DynamicEntryContainer>
                ))}

                <AddButton onClick={addSpousePersonalGuarantor} style={{ marginTop: theme.spacing.lg }}>
                  <FiPlus /> Ajouter un autre garant du conjoint
                </AddButton>

                <FormGroup style={{ marginTop: theme.spacing.lg }}>
                  <Label>Documents des garants du conjoint</Label>
                  <FileUploadArea>
                    <FiFileText size={24} color={theme.colors.primary} />
                    <FileUploadText>
                      Pièces d'identité, justificatifs des garants du conjoint...
                    </FileUploadText>
                    <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
                  </FileUploadArea>
                </FormGroup>
              </FormSection>
            )}
          </>
        );

      case 8: // Loyer Anticipé
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiClock />
                Loyer Anticipé
              </FormTitle>
              <FormDescription>
                Informations sur le paiement anticipé du loyer
              </FormDescription>
            </FormHeader>

            <FormGrid>
              <FormGroup>
                <Label>Nombre de mois de loyer anticipé</Label>
                <Select
                  value={formData.advance_rent_months}
                  onChange={e => setFormData({ ...formData, advance_rent_months: e.target.value })}
                >
                  <option value="">Sélectionner</option>
                  <option value="1">1 mois</option>
                  <option value="2">2 mois</option>
                  <option value="3">3 mois</option>
                  <option value="6">6 mois</option>
                  <option value="12">12 mois</option>
                  <option value="none">Aucun</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Modalités de paiement</Label>
                <Select
                  value={formData.advance_payment_terms}
                  onChange={e => setFormData({ ...formData, advance_payment_terms: e.target.value })}
                >
                  <option value="">Sélectionner</option>
                  <option value="lump_sum">Paiement unique</option>
                  <option value="installments">Paiements échelonnés</option>
                  <option value="deducted">Déduit des loyers futurs</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Révision périodique</Label>
                <Select
                  value={formData.advance_periodic_review}
                  onChange={e => setFormData({ ...formData, advance_periodic_review: e.target.value })}
                >
                  <option value="">Sélectionner</option>
                  <option value="quarterly">Trimestrielle</option>
                  <option value="semi_annual">Semestrielle</option>
                  <option value="annual">Annuelle</option>
                  <option value="none">Aucune</option>
                </Select>
              </FormGroup>
            </FormGrid>

            <FormGroup>
              <Label>Conditions spécifiques</Label>
              <TextArea
                value={formData.advance_specific_conditions}
                onChange={e => setFormData({ ...formData, advance_specific_conditions: e.target.value })}
                placeholder="Conditions particulières du loyer anticipé..."
              />
            </FormGroup>

            <FormSection>
              <SectionTitle>Justificatifs de paiement</SectionTitle>
              <FileUploadArea>
                <FiFileText size={32} color={theme.colors.primary} />
                <FileUploadText>
                  Preuve de paiement, virements bancaires...
                </FileUploadText>
                <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
              </FileUploadArea>
            </FormSection>
          </>
        );

      case 9: // Assurance Locative
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiShield />
                Assurance Locative
              </FormTitle>
              <FormDescription>
                Détails de votre assurance habitation
              </FormDescription>
            </FormHeader>

            <FormGrid>
              <FormGroup>
                <Input
                  type="text"
                  label="Compagnie d'assurance *"
                  value={formData.insurance_company}
                  onChange={e => setFormData({ ...formData, insurance_company: e.target.value })}
                  required
                  fullWidth
                />
              </FormGroup>

              <FormGroup>
                <Input
                  type="number"
                  label="Montant de la couverture ( FCFA) *"
                  value={formData.coverage_amount}
                  onChange={e => setFormData({ ...formData, coverage_amount: e.target.value })}
                  icon={<FiDollarSign />}
                  required
                  fullWidth
                />
              </FormGroup>

              <FormGroup>
                <Input
                  type="text"
                  label="Contacts de l'assurance"
                  value={formData.insurance_contacts}
                  onChange={e => setFormData({ ...formData, insurance_contacts: e.target.value })}
                  icon={<FiPhone />}
                  fullWidth
                />
              </FormGroup>

              <FormGroup>
                <Label>Révision périodique</Label>
                <Select
                  value={formData.insurance_periodic_review}
                  onChange={e => setFormData({ ...formData, insurance_periodic_review: e.target.value })}
                >
                  <option value="">Sélectionner</option>
                  <option value="monthly">Mensuelle</option>
                  <option value="quarterly">Trimestrielle</option>
                  <option value="annual">Annuelle</option>
                  <option value="none">Aucune</option>
                </Select>
              </FormGroup>
            </FormGrid>

            <FormGroup>
              <Label>Détails de l'assurance</Label>
              <TextArea
                value={formData.rental_insurance_details}
                onChange={e => setFormData({ ...formData, rental_insurance_details: e.target.value })}
                placeholder="Couvertures incluses, exclusions, franchise..."
              />
            </FormGroup>

            <FormSection>
              <SectionTitle>Documents d'assurance</SectionTitle>
              <FileUploadArea>
                <FiFileText size={32} color={theme.colors.primary} />
                <FileUploadText>
                  Attestation d'assurance, contrat, conditions générales...
                </FileUploadText>
                <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
              </FileUploadArea>
            </FormSection>
          </>
        );

      case 10: // Autres Garanties
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiFileText />
                Autres Garanties
              </FormTitle>
              <FormDescription>
                Autres types de garanties disponibles
              </FormDescription>
            </FormHeader>

            <FormGrid>
              <FormGroup>
                <Label>Type de garantie</Label>
                <Select
                  value={formData.other_guarantee_type}
                  onChange={e => setFormData({ ...formData, other_guarantee_type: e.target.value })}
                >
                  <option value="">Sélectionner</option>
                  <option value="investment_bond">Obligations d'investissement</option>
                  <option value="real_estate">Garantie immobilière</option>
                  <option value="professional">Garantie professionnelle</option>
                  <option value="solidarity">Caution solidaire</option>
                  <option value="other">Autre</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Input
                  type="number"
                  label="Montant de la garantie ( FCFA)"
                  value={formData.other_guarantee_amount}
                  onChange={e => setFormData({ ...formData, other_guarantee_amount: e.target.value })}
                  icon={<FiDollarSign />}
                  fullWidth
                />
              </FormGroup>
            </FormGrid>

            <FormGroup>
              <Label>Conditions spécifiques</Label>
              <TextArea
                value={formData.other_specific_conditions}
                onChange={e => setFormData({ ...formData, other_specific_conditions: e.target.value })}
                placeholder="Détails sur cette garantie supplémentaire..."
              />
            </FormGroup>

            <FormSection>
              <SectionTitle>Documents justificatifs</SectionTitle>
              <FileUploadArea>
                <FiFileText size={32} color={theme.colors.primary} />
                <FileUploadText>
                  Attestations, contrats, justificatifs divers...
                </FileUploadText>
                <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
              </FileUploadArea>
            </FormSection>
          </>
        );

      case 11: // Engagements Financiers/Dettes
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiAlertCircle />
                Engagements Financiers & Dettes
              </FormTitle>
              <FormDescription>
                Détaillez tous vos engagements financiers et dettes en cours
              </FormDescription>
            </FormHeader>

            {/* Liste dynamique des dettes */}
            {debts.map((debt, index) => (
              <DynamicEntryContainer key={debt.id}>
                <EntryHeader>
                  <EntryTitle>
                    <FiAlertCircle style={{ display: 'inline', marginRight: theme.spacing.xs }} />
                    Engagement/Dette <EntryNumber>#{index + 1}</EntryNumber>
                  </EntryTitle>
                  <RemoveButton
                    onClick={() => removeDebt(debt.id)}
                    disabled={debts.length === 1}
                    title={debts.length === 1 ? "Vous devez avoir au moins une entrée" : "Supprimer cet engagement"}
                  >
                    <FiTrash2 /> Retirer
                  </RemoveButton>
                </EntryHeader>

                <FormGrid>
                  <FormGroup>
                    <Label>Type de dette *</Label>
                    <Select
                      value={debt.type}
                      onChange={e => updateDebt(debt.id, 'type', e.target.value)}
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="mortgage">Prêt immobilier</option>
                      <option value="car_loan">Prêt automobile</option>
                      <option value="personal_loan">Prêt personnel</option>
                      <option value="credit_card">Crédit renouvelable</option>
                      <option value="student_loan">Prêt étudiant</option>
                      <option value="other">Autre</option>
                      <option value="none">Aucune dette</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="number"
                      label="Montant total ( FCFA) *"
                      value={debt.total_amount}
                      onChange={e => updateDebt(debt.id, 'total_amount', e.target.value)}
                      icon={<FiDollarSign />}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="number"
                      label="Montant de l'échéance ( FCFA)"
                      value={debt.due_date_amount}
                      onChange={e => updateDebt(debt.id, 'due_date_amount', e.target.value)}
                      icon={<FiDollarSign />}
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Fréquence de paiement</Label>
                    <Select
                      value={debt.payment_frequency}
                      onChange={e => updateDebt(debt.id, 'payment_frequency', e.target.value)}
                    >
                      <option value="">Sélectionner</option>
                      <option value="monthly">Mensuel</option>
                      <option value="quarterly">Trimestriel</option>
                      <option value="annual">Annuel</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="date"
                      label="Début de l'engagement"
                      value={debt.start_date}
                      onChange={e => updateDebt(debt.id, 'start_date', e.target.value)}
                      icon={<FiCalendar />}
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="date"
                      label="Fin de l'engagement"
                      value={debt.end_date}
                      onChange={e => updateDebt(debt.id, 'end_date', e.target.value)}
                      icon={<FiCalendar />}
                      fullWidth
                    />
                  </FormGroup>
                </FormGrid>

                <FormGroup>
                  <Label>Commentaires</Label>
                  <TextArea
                    value={debt.comments}
                    onChange={e => updateDebt(debt.id, 'comments', e.target.value)}
                    placeholder="Informations complémentaires sur cet engagement..."
                  />
                </FormGroup>
              </DynamicEntryContainer>
            ))}

            <AddButton onClick={addDebt}>
              <FiPlus /> Ajouter un autre engagement/dette
            </AddButton>

            <FormSection style={{ marginTop: theme.spacing.xl }}>
              <SectionTitle>Justificatifs de dettes</SectionTitle>
              <FileUploadArea>
                <FiFileText size={32} color={theme.colors.primary} />
                <FileUploadText>
                  Tableaux d'amortissement, contrats de prêt, relevés...
                </FileUploadText>
                <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
              </FileUploadArea>
            </FormSection>
          </>
        );

      case 12: // Historique de Résidence
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiMapPin />
                Historique de Résidence
              </FormTitle>
              <FormDescription>
                Informations sur vos précédentes résidences. Ajoutez votre résidence actuelle et vos anciens logements.
              </FormDescription>
            </FormHeader>

            {residenceHistory.map((residence, index) => (
              <DynamicEntryContainer key={residence.id}>
                <EntryHeader>
                  <EntryTitle>
                    <FiMapPin style={{ display: 'inline', marginRight: theme.spacing.xs }} />
                    Résidence <EntryNumber>#{index + 1}</EntryNumber>
                    {index === 0 && <span style={{ marginLeft: theme.spacing.xs, fontSize: theme.typography.sizes.sm, color: theme.colors.accent }}>(Actuelle ou récente)</span>}
                  </EntryTitle>
                  <RemoveButton
                    onClick={() => removeResidence(residence.id)}
                    disabled={residenceHistory.length === 1}
                  >
                    <FiTrash2 /> Retirer
                  </RemoveButton>
                </EntryHeader>

                <FormGrid>
                  <FormGroup>
                    <Input
                      type="text"
                      label="Adresse *"
                      value={residence.address}
                      onChange={e => updateResidence(residence.id, 'address', e.target.value)}
                      icon={<FiMapPin />}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="text"
                      label="Ville *"
                      value={residence.city}
                      onChange={e => updateResidence(residence.id, 'city', e.target.value)}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="number"
                      label="Loyer mensuel ( FCFA) *"
                      value={residence.monthly_rent}
                      onChange={e => updateResidence(residence.id, 'monthly_rent', e.target.value)}
                      icon={<FiDollarSign />}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="text"
                      label="Période de résidence *"
                      value={residence.period}
                      onChange={e => updateResidence(residence.id, 'period', e.target.value)}
                      placeholder="Ex: Jan 2020 - Déc 2023"
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="text"
                      label="Propriétaire/Bailleur *"
                      value={residence.landlord_name}
                      onChange={e => updateResidence(residence.id, 'landlord_name', e.target.value)}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="text"
                      label="Adresse du propriétaire"
                      value={residence.landlord_address}
                      onChange={e => updateResidence(residence.id, 'landlord_address', e.target.value)}
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="tel"
                      label="Téléphone du propriétaire"
                      value={residence.landlord_phone}
                      onChange={e => updateResidence(residence.id, 'landlord_phone', e.target.value)}
                      icon={<FiPhone />}
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="email"
                      label="Email du propriétaire"
                      value={residence.landlord_email}
                      onChange={e => updateResidence(residence.id, 'landlord_email', e.target.value)}
                      icon={<FiMail />}
                      fullWidth
                    />
                  </FormGroup>
                </FormGrid>

                <FormGroup>
                  <Label>Raison du départ</Label>
                  <TextArea
                    value={residence.reason_departure}
                    onChange={e => updateResidence(residence.id, 'reason_departure', e.target.value)}
                    placeholder="Pourquoi avez-vous quitté cette résidence..."
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Commentaires</Label>
                  <TextArea
                    value={residence.comments}
                    onChange={e => updateResidence(residence.id, 'comments', e.target.value)}
                    placeholder="Informations complémentaires..."
                  />
                </FormGroup>
              </DynamicEntryContainer>
            ))}

            <AddButton onClick={addResidence}>
              <FiPlus /> Ajouter une autre résidence (historique)
            </AddButton>

            <FormSection>
              <SectionTitle>Justificatifs de résidence</SectionTitle>
              <FileUploadArea>
                <FiFileText size={32} color={theme.colors.primary} />
                <FileUploadText>
                  Anciens baux, quittances de loyer, attestations du propriétaire...
                </FileUploadText>
                <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
              </FileUploadArea>
            </FormSection>
          </>
        );

      case 13: // Historique de Paiement de Loyer
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiDollarSign />
                Historique de Paiement de Loyer
              </FormTitle>
              <FormDescription>
                Informations sur vos habitudes de paiement des loyers
              </FormDescription>
            </FormHeader>

            <FormGrid>
              <FormGroup>
                <Input
                  type="date"
                  label="Date de paiement habituelle"
                  value={formData.rent_payment_date}
                  onChange={e => setFormData({ ...formData, rent_payment_date: e.target.value })}
                  icon={<FiCalendar />}
                  fullWidth
                />
              </FormGroup>

              <FormGroup>
                <Label>Méthode de paiement</Label>
                <Select
                  value={formData.rent_payment_method}
                  onChange={e => setFormData({ ...formData, rent_payment_method: e.target.value })}
                >
                  <option value="">Sélectionner</option>
                  <option value="transfer">Virement bancaire</option>
                  <option value="check">Chèque</option>
                  <option value="cash">Espèces</option>
                  <option value="automatic">Prélèvement automatique</option>
                  <option value="card">Carte bancaire</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Avez-vous des dettes de loyer impayées ? *</Label>
                <Select
                  value={formData.rent_debts}
                  onChange={e => setFormData({ ...formData, rent_debts: e.target.value })}
                  required
                >
                  <option value="non">Non</option>
                  <option value="oui">Oui</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Avez-vous déjà été expulsé ? *</Label>
                <Select
                  value={formData.evicted}
                  onChange={e => setFormData({ ...formData, evicted: e.target.value })}
                  required
                >
                  <option value="non">Non</option>
                  <option value="oui">Oui</option>
                </Select>
              </FormGroup>

              {formData.evicted === 'oui' && (
                <FormGroup>
                  <Input
                    type="text"
                    label="Année d'expulsion"
                    value={formData.year_of_eviction}
                    onChange={e => setFormData({ ...formData, year_of_eviction: e.target.value })}
                    placeholder="AAAA"
                    fullWidth
                  />
                </FormGroup>
              )}
            </FormGrid>

            <FormSection>
              <SectionTitle>Justificatifs de paiement</SectionTitle>
              <FileUploadArea>
                <FiFileText size={32} color={theme.colors.primary} />
                <FileUploadText>
                  Quittances de loyer, relevés bancaires montrant les paiements...
                </FileUploadText>
                <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
              </FileUploadArea>
            </FormSection>
          </>
        );

      case 14: // Antécédents Criminels
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiAlertCircle />
                Antécédents Criminels
              </FormTitle>
              <FormDescription>
                Déclaration des antécédents judiciaires (si applicable)
              </FormDescription>
            </FormHeader>

            <FormGrid>
              <FormGroup>
                <Label>Nature de l'infraction</Label>
                <Select
                  value={formData.nature_of_infraction}
                  onChange={e => setFormData({ ...formData, nature_of_infraction: e.target.value })}
                >
                  <option value="">Aucune infraction</option>
                  <option value="minor">Infraction mineure</option>
                  <option value="misdemeanor">Délit</option>
                  <option value="felony">Crime</option>
                </Select>
              </FormGroup>

              {formData.nature_of_infraction && formData.nature_of_infraction !== '' && (
                <>
                  <FormGroup>
                    <Label>Gravité de l'infraction</Label>
                    <Select
                      value={formData.severity_of_infraction}
                      onChange={e => setFormData({ ...formData, severity_of_infraction: e.target.value })}
                    >
                      <option value="">Sélectionner</option>
                      <option value="low">Faible</option>
                      <option value="medium">Moyenne</option>
                      <option value="high">Élevée</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="date"
                      label="Date de l'infraction"
                      value={formData.date_of_infraction}
                      onChange={e => setFormData({ ...formData, date_of_infraction: e.target.value })}
                      icon={<FiCalendar />}
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Type de condamnation</Label>
                    <Select
                      value={formData.type_of_conviction}
                      onChange={e => setFormData({ ...formData, type_of_conviction: e.target.value })}
                    >
                      <option value="">Sélectionner</option>
                      <option value="fine">Amende</option>
                      <option value="suspended">Peine avec sursis</option>
                      <option value="probation">Probation</option>
                      <option value="imprisonment">Emprisonnement</option>
                      <option value="community_service">Travaux d'intérêt général</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Programme de réhabilitation</Label>
                    <Select
                      value={formData.rehabilitation_program}
                      onChange={e => setFormData({ ...formData, rehabilitation_program: e.target.value })}
                    >
                      <option value="">Sélectionner</option>
                      <option value="completed">Programme complété</option>
                      <option value="in_progress">En cours</option>
                      <option value="none">Aucun</option>
                    </Select>
                  </FormGroup>
                </>
              )}
            </FormGrid>

            <FormGroup>
              <Label>Commentaires</Label>
              <TextArea
                value={formData.infraction_comments}
                onChange={e => setFormData({ ...formData, infraction_comments: e.target.value })}
                placeholder="Contexte, circonstances atténuantes, démarches entreprises..."
              />
            </FormGroup>

            <FormSection>
              <SectionTitle>Documents justificatifs</SectionTitle>
              <FileUploadArea>
                <FiFileText size={32} color={theme.colors.primary} />
                <FileUploadText>
                  Extrait de casier judiciaire, certificats de réhabilitation...
                </FileUploadText>
                <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
              </FileUploadArea>
            </FormSection>
          </>
        );

      case 15: // Références Relationnelles
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiPhone />
                Références Relationnelles
              </FormTitle>
              <FormDescription>
                Personnes pouvant attester de votre fiabilité. Ajoutez au moins 2 références professionnelles ou personnelles.
              </FormDescription>
            </FormHeader>

            {references.map((reference, index) => (
              <DynamicEntryContainer key={reference.id}>
                <EntryHeader>
                  <EntryTitle>
                    <FiUser style={{ display: 'inline', marginRight: theme.spacing.xs }} />
                    Référence <EntryNumber>#{index + 1}</EntryNumber>
                  </EntryTitle>
                  <RemoveButton
                    onClick={() => removeReference(reference.id)}
                    disabled={references.length === 1}
                  >
                    <FiTrash2 /> Retirer
                  </RemoveButton>
                </EntryHeader>

                <FormGrid>
                  <FormGroup>
                    <Input
                      type="text"
                      label="Nom complet de la référence *"
                      value={reference.name}
                      onChange={e => updateReference(reference.id, 'name', e.target.value)}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="text"
                      label="Adresse de la référence"
                      value={reference.address}
                      onChange={e => updateReference(reference.id, 'address', e.target.value)}
                      icon={<FiMapPin />}
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="tel"
                      label="Téléphone de la référence *"
                      value={reference.phone}
                      onChange={e => updateReference(reference.id, 'phone', e.target.value)}
                      icon={<FiPhone />}
                      required
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="email"
                      label="Email de la référence"
                      value={reference.email}
                      onChange={e => updateReference(reference.id, 'email', e.target.value)}
                      icon={<FiMail />}
                      fullWidth
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Nature de la relation *</Label>
                    <Select
                      value={reference.relationship}
                      onChange={e => updateReference(reference.id, 'relationship', e.target.value)}
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="employer">Employeur actuel</option>
                      <option value="former_employer">Ancien employeur</option>
                      <option value="landlord">Ancien propriétaire</option>
                      <option value="colleague">Collègue</option>
                      <option value="professor">Professeur/Enseignant</option>
                      <option value="friend">Ami(e) de longue date</option>
                      <option value="professional">Contact professionnel</option>
                      <option value="other">Autre</option>
                    </Select>
                  </FormGroup>
                </FormGrid>

                <FormGroup>
                  <Label>Commentaires</Label>
                  <TextArea
                    value={reference.comments}
                    onChange={e => updateReference(reference.id, 'comments', e.target.value)}
                    placeholder="Depuis combien de temps vous connaissez-vous, contexte de la relation..."
                  />
                </FormGroup>
              </DynamicEntryContainer>
            ))}

            <AddButton onClick={addReference}>
              <FiPlus /> Ajouter une autre référence
            </AddButton>

            <FormSection>
              <SectionTitle>Lettre de recommandation</SectionTitle>
              <FileUploadArea>
                <FiFileText size={32} color={theme.colors.primary} />
                <FileUploadText>
                  Lettre de recommandation, attestation...
                </FileUploadText>
                <Input type="file" accept="image/*,application/pdf" multiple fullWidth style={{ marginTop: theme.spacing.md }} />
              </FileUploadArea>
            </FormSection>
          </>
        );

      case 16: // Pièces Jointes - Récapitulatif
        return (
          <>
            <FormHeader>
              <FormTitle>
                <FiFileText />
                Pièces Jointes - Récapitulatif
              </FormTitle>
              <FormDescription>
                Vue d'ensemble de tous les documents attachés à votre dossier
              </FormDescription>
            </FormHeader>

            <FormSection>
              <SectionTitle>📄 Catégorie 1 - Informations Personnelles</SectionTitle>
              <FileUploadArea>
                <FiFileText size={24} color={theme.colors.primary} />
                <FileUploadText>
                  <strong>Documents requis (Obligatoire):</strong><br/>
                  • Pièce d'identité (CNI, Passeport, Carte de séjour)<br/>
                  • Justificatif de domicile (moins de 3 mois)<br/>
                  • Livret de famille (si applicable)
                </FileUploadText>
              </FileUploadArea>
            </FormSection>

            <FormSection>
              <SectionTitle>💼 Catégorie 3 - Situations Professionnelles</SectionTitle>
              <FileUploadArea>
                <FiFileText size={24} color={theme.colors.primary} />
                <FileUploadText>
                  <strong>Pour Salarié:</strong><br/>
                  • Bulletins de salaire (3 derniers mois) - Obligatoire<br/>
                  • Contrat de travail - Obligatoire<br/>
                  • Attestation employeur - Optionnel<br/><br/>
                  <strong>Pour Entrepreneur:</strong><br/>
                  • Extrait KBIS ou SIRET - Obligatoire<br/>
                  • Déclaration fiscale (2 dernières années) - Obligatoire<br/>
                  • Attestation comptable - Optionnel<br/><br/>
                  <strong>Pour Secteur Informel:</strong><br/>
                  • Déclaration sur l'honneur des revenus - Obligatoire<br/>
                  • Attestation de témoin (activité professionnelle) - Optionnel
                </FileUploadText>
              </FileUploadArea>
            </FormSection>

            <FormSection>
              <SectionTitle>💰 Catégorie 4 - Revenus Mensuels</SectionTitle>
              <FileUploadArea>
                <FiFileText size={24} color={theme.colors.primary} />
                <FileUploadText>
                  <strong>Documents requis:</strong><br/>
                  • Avis d'imposition (dernière année) - Obligatoire<br/>
                  • Relevés bancaires (6 derniers mois) - Obligatoire pour Entrepreneur/Informel<br/>
                  • Justificatifs d'autres revenus (allocations, pensions, etc.) - Si applicable
                </FileUploadText>
              </FileUploadArea>
            </FormSection>

            <FormSection>
              <SectionTitle>📈 Catégorie 5 - Capacité d'Épargne</SectionTitle>
              <FileUploadArea>
                <FiFileText size={24} color={theme.colors.primary} />
                <FileUploadText>
                  <strong>Documents requis:</strong><br/>
                  • Relevés de compte épargne (3 derniers mois) - Si applicable<br/>
                  • Attestation d'épargne (banque, microfinance) - Si applicable<br/>
                  • Justificatifs de placements financiers - Si applicable
                </FileUploadText>
              </FileUploadArea>
            </FormSection>

            <FormSection>
              <SectionTitle>🛡️ Catégorie 6 - Garanties Financières</SectionTitle>
              <FileUploadArea>
                <FiFileText size={24} color={theme.colors.primary} />
                <FileUploadText>
                  <strong>Documents requis (si applicable):</strong><br/>
                  • Attestation de caution bancaire<br/>
                  • Contrat de garantie locative (GRL, Visale, etc.)<br/>
                  • Justificatif de dépôt de garantie
                </FileUploadText>
              </FileUploadArea>
            </FormSection>

            <FormSection>
              <SectionTitle>👥 Catégorie 7 - Garanties Personnelles</SectionTitle>
              <FileUploadArea>
                <FiFileText size={24} color={theme.colors.primary} />
                <FileUploadText>
                  <strong>Documents du garant (Obligatoire pour Secteur Informel):</strong><br/>
                  • Pièce d'identité du garant<br/>
                  • Justificatifs de revenus du garant (3 derniers mois)<br/>
                  • Avis d'imposition du garant<br/>
                  • Attestation d'engagement de caution signée
                </FileUploadText>
              </FileUploadArea>
            </FormSection>

            <FormSection>
              <SectionTitle>⏰ Catégorie 8 - Loyer Anticipé</SectionTitle>
              <FileUploadArea>
                <FiFileText size={24} color={theme.colors.primary} />
                <FileUploadText>
                  <strong>Documents requis (si paiement anticipé):</strong><br/>
                  • Preuve de virement bancaire<br/>
                  • Reçu de paiement<br/>
                  • Attestation bancaire de disponibilité des fonds
                </FileUploadText>
              </FileUploadArea>
            </FormSection>

            <FormSection>
              <SectionTitle>🔒 Catégorie 9 - Assurance Locative</SectionTitle>
              <FileUploadArea>
                <FiFileText size={24} color={theme.colors.primary} />
                <FileUploadText>
                  <strong>Documents requis (Obligatoire):</strong><br/>
                  • Attestation d'assurance habitation<br/>
                  • Contrat d'assurance<br/>
                  • Conditions générales de l'assurance<br/>
                  • Justificatif de paiement de la prime
                </FileUploadText>
              </FileUploadArea>
            </FormSection>

            <FormSection>
              <SectionTitle>📋 Catégorie 10 - Autres Garanties</SectionTitle>
              <FileUploadArea>
                <FiFileText size={24} color={theme.colors.primary} />
                <FileUploadText>
                  <strong>Documents complémentaires (si applicable):</strong><br/>
                  • Attestation de non-endettement locatif<br/>
                  • Certificat de bonne moralité<br/>
                  • Références de précédents bailleurs<br/>
                  • Tout autre document justificatif pertinent
                </FileUploadText>
              </FileUploadArea>
            </FormSection>

            <FormSection>
              <SectionTitle>⚠️ Catégorie 11 - Engagements Financiers/Dettes</SectionTitle>
              <FileUploadArea>
                <FiFileText size={24} color={theme.colors.primary} />
                <FileUploadText>
                  <strong>Documents requis (si applicable):</strong><br/>
                  • Tableaux d'amortissement des prêts en cours<br/>
                  • Contrats de crédit (immobilier, auto, consommation)<br/>
                  • Relevés de crédit/endettement<br/>
                  • Attestation bancaire de capacité d'emprunt restante
                </FileUploadText>
              </FileUploadArea>
            </FormSection>

            <FormSection>
              <SectionTitle>🏠 Catégorie 12 - Historique de Résidence</SectionTitle>
              <FileUploadArea>
                <FiFileText size={24} color={theme.colors.primary} />
                <FileUploadText>
                  <strong>Documents requis:</strong><br/>
                  • Anciens contrats de bail (2 dernières locations)<br/>
                  • Quittances de loyer (6 derniers mois du logement précédent)<br/>
                  • Attestation du propriétaire/bailleur précédent<br/>
                  • Certificat de régularité de paiement des loyers
                </FileUploadText>
              </FileUploadArea>
            </FormSection>

            <FormSection>
              <SectionTitle>💳 Catégorie 13 - Historique de Paiement de Loyer</SectionTitle>
              <FileUploadArea>
                <FiFileText size={24} color={theme.colors.primary} />
                <FileUploadText>
                  <strong>Documents requis:</strong><br/>
                  • Quittances de loyer (12 derniers mois minimum)<br/>
                  • Relevés bancaires montrant les virements de loyer<br/>
                  • Attestation de non-dette locative<br/>
                  • Historique de paiements (si disponible auprès d'agence)
                </FileUploadText>
              </FileUploadArea>
            </FormSection>

            <FormSection>
              <SectionTitle>⚖️ Catégorie 14 - Antécédents Criminels</SectionTitle>
              <FileUploadArea>
                <FiFileText size={24} color={theme.colors.primary} />
                <FileUploadText>
                  <strong>Documents requis (Obligatoire):</strong><br/>
                  • Extrait de casier judiciaire (Bulletin n°3)<br/>
                  • Certificat de bonne vie et mœurs<br/>
                  • Certificat de réhabilitation (si applicable)<br/>
                  • Attestation de non-condamnation
                </FileUploadText>
              </FileUploadArea>
            </FormSection>

            <FormSection>
              <SectionTitle>📞 Catégorie 15 - Références Relationnelles</SectionTitle>
              <FileUploadArea>
                <FiFileText size={24} color={theme.colors.primary} />
                <FileUploadText>
                  <strong>Documents requis (minimum 2 références):</strong><br/>
                  • Lettres de recommandation (employeur, précédent bailleur)<br/>
                  • Attestations de moralité<br/>
                  • Coordonnées de références vérifiables<br/>
                  • Lettres de soutien (si applicable)
                </FileUploadText>
              </FileUploadArea>
            </FormSection>

            <ProgressInfo style={{ marginTop: theme.spacing.xl }}>
              ✅ Vérifiez que tous vos documents sont bien attachés avant de soumettre votre candidature
            </ProgressInfo>
          </>
        );

      default:
        return (
          <FormHeader>
            <FormTitle>
              <FiFileText />
              Catégorie {currentCategory}
            </FormTitle>
            <FormDescription>
              Formulaire en cours de développement pour {categories[currentCategory - 1]?.name}
            </FormDescription>
            <ProgressInfo>
              ℹ️ Cette section sera disponible prochainement. Utilisez les boutons de navigation pour parcourir les autres catégories.
            </ProgressInfo>
          </FormHeader>
        );
    }
  };

  const completedCount = categories.filter(c => c.completed).length;
  const progress = Math.round((completedCount / categories.length) * 100);

  // Contenu du formulaire (utilisé avec ou sans DashboardLayout)
  const formContent = (
    <>
      {/* Modal de Clause de Non-responsabilité - Affiché au chargement (désactivé en readonly) */}
      {!readonly && (
        <DisclaimerModal
          isOpen={showDisclaimerModal}
          onAccept={handleDisclaimerAccept}
          onDecline={handleDisclaimerDecline}
        />
      )}

      {/* Modal de Déclaration de Sincérité - Affiché avant soumission (désactivé en readonly) */}
      {!readonly && (
        <SincerityDeclarationModal
          isOpen={showSincerityModal}
          candidateName={`${formData.first_name} ${formData.last_name}`}
          organizationName="Housing Scoring System"
          onValidate={handleSincerityValidate}
          onCancel={handleSincerityCancel}
        />
      )}

      {/* Afficher le formulaire SEULEMENT si le disclaimer a été accepté (ou en mode readonly) */}
      {disclaimerAccepted && (
        <ApplicationContainer>
          <Sidebar>
            <SidebarTitle>📋 Dossier de Candidature</SidebarTitle>
            <ProgressInfo>
              Progression: {completedCount}/{categories.length} ({progress}%)
            </ProgressInfo>
            <CategoryList>
              {categories.map(category => (
                <CategoryItem
                  key={category.id}
                  $active={currentCategory === category.id}
                  $completed={category.completed}
                  onClick={() => setCurrentCategory(category.id)}
                >
                  <CategoryNumber $active={currentCategory === category.id} $completed={category.completed}>
                    {category.completed ? <FiCheckCircle /> : category.id}
                  </CategoryNumber>
                  {category.icon}
                  <CategoryName $active={currentCategory === category.id}>
                    {category.name}
                  </CategoryName>
                  {category.completed && <CompletionBadge>✓</CompletionBadge>}
                </CategoryItem>
              ))}
            </CategoryList>
          </Sidebar>

          <FormContent $readonly={readonly}>
            {renderCategoryForm()}

            {/* Masquer les boutons de navigation en mode readonly */}
            {!readonly && (
              <NavigationButtons>
                {currentCategory > 1 ? (
                  <Button
                    variant="secondary"
                    onClick={handlePrevious}
                    icon={<FiChevronRight style={{ transform: 'rotate(180deg)' }} />}
                  >
                    Précédent
                  </Button>
                ) : (
                  <div />
                )}

                {currentCategory < categories.length ? (
                  <Button
                    variant="primary"
                    onClick={handleNext}
                    icon={<FiChevronRight />}
                    style={{ marginLeft: 'auto' }}
                  >
                    Suivant
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    icon={<FiCheckCircle />}
                    style={{ marginLeft: 'auto' }}
                  >
                    Soumettre la candidature
                  </Button>
                )}
              </NavigationButtons>
            )}
          </FormContent>
        </ApplicationContainer>
      )}

      {/* Message si le disclaimer n'est pas encore accepté (désactivé en readonly) */}
      {!readonly && !disclaimerAccepted && !showDisclaimerModal && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: theme.colors.text
        }}>
          <p>Veuillez accepter les conditions pour continuer.</p>
        </div>
      )}
    </>
  );

  // En mode readonly, pas de DashboardLayout (car fourni par la page parent)
  return readonly ? formContent : <DashboardLayout>{formContent}</DashboardLayout>;
};
